import { useRef, useEffect, useCallback, useState } from 'react';
import type { ToothData } from '../../types/records';

// ── Selection types emitted by the chart ──
export interface ChartSelectionData {
  selectedTeeth: number[];
  selectedSurfaces: Record<string, string[]>; // { "16": ["occlusal","buccal"] }
  selectedParts: Record<string, string[]>;    // { "16": ["crown"] }
  toothTypes: Record<string, string>;         // { "16": "molar" }
}

interface DentalChartProProps {
  teethData: Record<string, ToothData>;
  onSelectionChange?: (sel: ChartSelectionData) => void;
  onChartReady?: () => void;
  jawView?: 'full' | 'upper' | 'lower';
  patientId?: string | null;
  className?: string;
}

// Use a unique symbol key on window to avoid namespace pollution (#38)
const CHART_PRO_KEY = '__dentalChartPro';

// Determine valid origin for postMessage security (#39/#40)
function getExpectedOrigin(): string {
  return window.location.origin;
}

export default function DentalChartPro({
  teethData,
  onSelectionChange,
  onChartReady,
  jawView = 'full',
  patientId = null,
  className = '',
}: DentalChartProProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const pendingLoadRef = useRef<Record<string, ToothData> | null>(null);
  const prevTeethDataRef = useRef<Record<string, ToothData>>({});

  // ── Send message to iframe ──
  const sendMessage = useCallback((type: string, payload?: unknown) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type, payload }, '*');
    }
  }, []);

  // ── Load chart data into iframe ──
  const loadChartData = useCallback((data: Record<string, ToothData>) => {
    if (!isReady) {
      pendingLoadRef.current = data;
      return;
    }
    sendMessage('load-chart-data', { teeth_data: data });
  }, [isReady, sendMessage]);

  // ── Public API: apply state to a tooth ──
  const applyState = useCallback((
    toothFDI: number,
    stateCode: string,
    targetZone: string,
    surfaces: string[] = [],
    notes = '',
    metadata: Record<string, unknown> = {}
  ) => {
    sendMessage('apply-state', { toothFDI, stateCode, targetZone, surfaces, notes, metadata });
  }, [sendMessage]);

  // ── Public API: clear selections ──
  const clearSelections = useCallback(() => {
    sendMessage('clear-selections');
  }, [sendMessage]);

  // ── Listen for messages from iframe (#40: validate origin) ──
  useEffect(() => {
    const expectedOrigin = getExpectedOrigin();
    const handler = (event: MessageEvent) => {
      // Validate message origin (#40)
      if (event.origin !== expectedOrigin && event.origin !== 'null') return;

      const msg = event.data;
      if (!msg || !msg.type) return;

      switch (msg.type) {
        case 'dental-chart-ready':
          setIsReady(true);
          onChartReady?.();
          break;

        case 'dental-chart-selection':
          onSelectionChange?.(msg.payload as ChartSelectionData);
          break;

        case 'dental-chart-loaded':
          // Chart finished loading data
          break;

        case 'dental-chart-state-applied':
          // State was applied in the chart
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onSelectionChange, onChartReady]);

  // ── Timeout: retry ping and force ready if iframe never responds ──
  useEffect(() => {
    if (isReady) return;

    // Send periodic pings to iframe asking it to re-send ready
    const pingInterval = setInterval(() => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'ping' }, '*');
      }
    }, 1500);

    // Show timeout hint after 6 seconds
    const hintTimeout = setTimeout(() => setLoadTimeout(true), 6000);

    // Force ready after 12 seconds to prevent infinite loading
    const forceTimeout = setTimeout(() => {
      setIsReady(true);
      onChartReady?.();
      console.warn('[DentalChartPro] Forced ready after timeout — iframe may not have signaled.');
    }, 12000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(hintTimeout);
      clearTimeout(forceTimeout);
    };
  }, [isReady, onChartReady]);

  // ── Load pending data when chart becomes ready ──
  useEffect(() => {
    if (isReady && pendingLoadRef.current) {
      loadChartData(pendingLoadRef.current);
      pendingLoadRef.current = null;
    }
  }, [isReady, loadChartData]);

  // ── Smart reload: only send full data when teeth actually changed (#7) ──
  useEffect(() => {
    const prev = prevTeethDataRef.current;
    const curr = teethData;
    if (Object.keys(curr).length === 0) return;

    // Check if this is the same reference (from our own setTeethData)
    // or if something genuinely changed (e.g., refreshChart from backend)
    const prevKeys = Object.keys(prev);
    const currKeys = Object.keys(curr);
    const isNewData = prevKeys.length !== currKeys.length
      || currKeys.some(k => prev[k] !== curr[k]);

    if (isNewData) {
      loadChartData(curr);
      prevTeethDataRef.current = curr;
    }
  }, [teethData, loadChartData]);

  // ── Expose methods via global (#38: cleanup on unmount) ──
  useEffect(() => {
    (window as any)[CHART_PRO_KEY] = { applyState, clearSelections, sendMessage };
    return () => {
      delete (window as any)[CHART_PRO_KEY];
    };
  }, [applyState, clearSelections, sendMessage]);

  // ── Jaw view: change SVG viewBox in iframe ──
  useEffect(() => {
    if (isReady) {
      sendMessage('set-jaw-view', { view: jawView });
    }
  }, [jawView, isReady, sendMessage]);

  // ── Clear chart when patient changes (before new data arrives) ──
  const prevPatientIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (patientId && patientId !== prevPatientIdRef.current) {
      if (prevPatientIdRef.current !== null && isReady) {
        // Patient actually changed — clear chart visuals immediately
        sendMessage('clear-chart');
        prevTeethDataRef.current = {};
      }
      prevPatientIdRef.current = patientId;
    }
  }, [patientId, isReady, sendMessage]);

  return (
    <div className={`dental-chart-pro-container overflow-hidden ${className}`}>
      {!isReady && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm">
          {/* Skeleton tooth shapes */}
          <div className="flex gap-1.5 mb-4 opacity-20">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="w-5 h-7 rounded-sm bg-slate-300 dark:bg-slate-600 animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <svg className="h-4 w-4 animate-spin text-teal-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Chargement du schéma dentaire...
          </div>
          {loadTimeout && (
            <button
              onClick={() => {
                // Force reload the iframe
                if (iframeRef.current) {
                  setLoadTimeout(false);
                  iframeRef.current.src = '/dental-chart-embed.html?' + Date.now();
                }
              }}
              className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-600 dark:text-teal-400 border border-teal-500/20 transition-colors"
            >
              Recharger le schéma
            </button>
          )}
        </div>
      )}
      <iframe
        ref={iframeRef}
        src="/dental-chart-embed.html"
        title="Schéma dentaire professionnel"
        aria-label="Schéma dentaire interactif — cliquez pour sélectionner des dents"
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
}